<template>
  <div
    v-if="hasItems || hasFilter"
    class="active-filters"
    role="region"
    aria-labelledby="active-filters-label"
  >
    <span
      id="active-filters-label"
      class="active-filters__label"
    >
      Deine Filter:
    </span>
    <ul
      class="active-filters__list"
      aria-label="Aktive Filter"
    >
      <li
        v-for="(item, key) in items"
        :key="key"
        class="active-filters__item"
      >
        <FilterTag
          v-bind="item"
          @click="onRemove"
        />
      </li>
      <li class="active-filters__clear">
        <LinkButton
          tag="button"
          type="button"
          text
          class="active-filters__clear-button"
          @click="clear"
        >
          Alle Filter löschen
        </LinkButton>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { isEqual, remove } from 'lodash-es';
import { LinkButton } from '@i22team/td-longtail-vue-components';

interface ActiveFiltersProps {
  value?: SelectedTag[];
  hasFilter?: boolean;
}

const props = withDefaults(defineProps<ActiveFiltersProps>(), {
  value: () => [],
  hasFilter: false,
});

const emit = defineEmits<{
  remove: [value: SelectedTag['value'] | undefined];
  clear: [prevItems: SelectedTag[]];
}>();

const items = ref<SelectedTag[]>([...props.value]);

watch(
  () => props.value,
  (value) => {
    items.value = value;
  }
);

const hasItems = computed(() => items.value.length > 0);

function clear() {
  const prevItems = items.value;
  items.value = [];
  emit('clear', prevItems);
}

function onRemove(value: SelectedTag['value'] | undefined) {
  remove(items.value, (item) => isEqual(item.value, value));
  emit('remove', value);
}

defineExpose({
  items,
  hasItems,
  clear,
  onRemove,
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.active-filters {
  user-select: none;
  margin: $grid-base 0;

  &__label {
    display: inline-block;
    margin-right: 10px;
    font-weight: 700;
    width: 100%;
    margin-bottom: 0.5rem;
    font-size: inherit;

    @include mq($mq-sm) {
      width: auto;
      margin-bottom: 0;
    }
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: inline;

    @include mq($mq-sm) {
      display: inline;
    }
  }

  &__item {
    display: inline-block;
    margin-left: 0;
    margin-right: 10px;
    padding-bottom: 6px;
  }

  &__clear {
    display: block;
    padding-top: 6px;
    margin-left: 0;
    @include mq($mq-sm) {
      padding: 0;
      display: inline-block;
    }

    &-button {
      @include font-size(15px, 18px);
    }
  }
}
</style>
